import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Notification } from '@/types'
import { notificationService } from '@/services/notificationService'
import { useAuthStore } from './authStore'
import toast from 'react-hot-toast'

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  hasMore: boolean
  currentPage: number
  totalCount: number
}

interface NotificationActions {
  // Fetch notifications
  fetchNotifications: (params?: {
    page?: number
    limit?: number
    notification_type?: string
    is_read?: boolean
    priority?: string
    refresh?: boolean
  }) => Promise<void>

  // Fetch unread count
  fetchUnreadCount: () => Promise<void>

  // Mark as read
  markAsRead: (notificationId: number) => Promise<void>

  // Mark all as read
  markAllAsRead: () => Promise<void>

  // Delete notification
  deleteNotification: (notificationId: number) => Promise<void>

  // Get notifications by type
  getNotificationsByType: (
    type: string,
    params?: { page?: number; limit?: number }
  ) => Promise<void>

  // Get high priority notifications
  getHighPriorityNotifications: (params?: { page?: number; limit?: number }) => Promise<void>

  // Get urgent notifications
  getUrgentNotifications: (params?: { page?: number; limit?: number }) => Promise<void>

  // Get recent notifications
  getRecentNotifications: (params?: { page?: number; limit?: number }) => Promise<void>

  // Add notification (for real-time updates)
  addNotification: (notification: Notification) => void

  // Update notification
  updateNotification: (notificationId: number, updates: Partial<Notification>) => void

  // Remove notification
  removeNotification: (notificationId: number) => void

  // Clear all notifications
  clearNotifications: () => void

  // Set loading state
  setLoading: (loading: boolean) => void
}

type NotificationStore = NotificationState & NotificationActions

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      // State
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      hasMore: false,
      currentPage: 1,
      totalCount: 0,

      // Fetch notifications
      fetchNotifications: async (params = {}) => {
        const { refresh = false, page = 1, ...otherParams } = params

        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (!isAuthenticated) {
          console.warn('User not authenticated, skipping notification fetch')
          return
        }

        set({ isLoading: true })

        try {
          const response = await notificationService.getNotifications({
            page,
            limit: 20,
            ...otherParams,
          })

          set(state => ({
            notifications:
              refresh || page === 1
                ? response.results || []
                : [...(state.notifications || []), ...(response.results || [])],
            hasMore: !!response.next,
            currentPage: page,
            totalCount: response.count || 0,
            isLoading: false,
          }))

          // Update unread count
          get().fetchUnreadCount()
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = notificationService.handleError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      // Fetch unread count
      fetchUnreadCount: async () => {
        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (!isAuthenticated) {
          console.warn('User not authenticated, skipping unread count fetch')
          return
        }

        try {
          const response = await notificationService.getUnreadCount()
          set({ unreadCount: response.count || 0 })
        } catch (error) {
          console.warn('Failed to fetch unread count:', error)
        }
      },

      // Mark as read
      markAsRead: async (notificationId: number) => {
        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (!isAuthenticated) {
          console.warn('User not authenticated, skipping mark as read')
          return
        }

        try {
          await notificationService.markAsRead(notificationId)

          set(state => ({
            notifications: (state.notifications || []).map(notification =>
              notification.id === notificationId
                ? { ...notification, is_read: true, read_at: new Date().toISOString() }
                : notification
            ),
            unreadCount: Math.max(0, (state.unreadCount || 0) - 1),
          }))

          toast.success('Notification marked as read')
        } catch (error: any) {
          const errorMessage = notificationService.handleError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      // Mark all as read
      markAllAsRead: async () => {
        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (!isAuthenticated) {
          console.warn('User not authenticated, skipping mark all as read')
          return
        }

        try {
          await notificationService.markAllAsRead()

          set(state => ({
            notifications: (state.notifications || []).map(notification => ({
              ...notification,
              is_read: true,
              read_at: new Date().toISOString(),
            })),
            unreadCount: 0,
          }))

          toast.success('All notifications marked as read')
        } catch (error: any) {
          const errorMessage = notificationService.handleError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      // Delete notification
      deleteNotification: async (notificationId: number) => {
        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (!isAuthenticated) {
          console.warn('User not authenticated, skipping delete notification')
          return
        }

        try {
          await notificationService.deleteNotification(notificationId)

          set(state => {
            const notification = (state.notifications || []).find(n => n.id === notificationId)
            return {
              notifications: (state.notifications || []).filter(n => n.id !== notificationId),
              unreadCount:
                notification && !notification.is_read
                  ? Math.max(0, (state.unreadCount || 0) - 1)
                  : state.unreadCount || 0,
              totalCount: Math.max(0, (state.totalCount || 0) - 1),
            }
          })

          toast.success('Notification deleted')
        } catch (error: any) {
          const errorMessage = notificationService.handleError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      // Get notifications by type
      getNotificationsByType: async (type: string, params = {}) => {
        await get().fetchNotifications({ ...params, notification_type: type, refresh: true })
      },

      // Get high priority notifications
      getHighPriorityNotifications: async (params = {}) => {
        await get().fetchNotifications({ ...params, priority: 'high', refresh: true })
      },

      // Get urgent notifications
      getUrgentNotifications: async (params = {}) => {
        await get().fetchNotifications({ ...params, priority: 'urgent', refresh: true })
      },

      // Get recent notifications
      getRecentNotifications: async (params = {}) => {
        // Check if user is authenticated
        const { isAuthenticated } = useAuthStore.getState()
        if (!isAuthenticated) {
          console.warn('User not authenticated, skipping recent notifications fetch')
          return
        }

        set({ isLoading: true })

        try {
          const response = await notificationService.getRecentNotifications({
            page: params.page || 1,
            limit: params.limit || 20,
          })

          set({
            notifications: response.results || [],
            hasMore: !!response.next,
            currentPage: params.page || 1,
            totalCount: response.count || 0,
            isLoading: false,
          })
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = notificationService.handleError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      // Add notification (for real-time updates)
      addNotification: (notification: Notification) => {
        set(state => ({
          notifications: [notification, ...(state.notifications || [])],
          unreadCount: notification.is_read ? state.unreadCount || 0 : (state.unreadCount || 0) + 1,
          totalCount: (state.totalCount || 0) + 1,
        }))
      },

      // Update notification
      updateNotification: (notificationId: number, updates: Partial<Notification>) => {
        set(state => ({
          notifications: (state.notifications || []).map(notification =>
            notification.id === notificationId ? { ...notification, ...updates } : notification
          ),
        }))
      },

      // Remove notification
      removeNotification: (notificationId: number) => {
        set(state => {
          const notification = (state.notifications || []).find(n => n.id === notificationId)
          return {
            notifications: (state.notifications || []).filter(n => n.id !== notificationId),
            unreadCount:
              notification && !notification.is_read
                ? Math.max(0, (state.unreadCount || 0) - 1)
                : state.unreadCount || 0,
            totalCount: Math.max(0, (state.totalCount || 0) - 1),
          }
        })
      },

      // Clear all notifications
      clearNotifications: () => {
        set({
          notifications: [],
          unreadCount: 0,
          hasMore: false,
          currentPage: 1,
          totalCount: 0,
        })
      },

      // Set loading state
      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },
    }),
    {
      name: 'notification-storage',
      partialize: state => ({
        notifications: state.notifications?.slice(0, 50) || [], // Store only recent notifications
        unreadCount: state.unreadCount || 0,
      }),
    }
  )
)
